<template>
    <component :is="as">
        <slot :products="products"
            :product-count="productCount"
            :alternative-products="alternativeProducts"
            :has-next-page="hasNextPage"
            :load-more="loadMore"
            :get-ad-for-index="getAdForIndex"
            :show-cta-for-index="showCtaForIndex"
            :active-filters-count="activeFiltersCount"
            :is-loading="isLoading"
            :clear-filter="clearFilter" />
    </component>
</template>

<script lang="ts" setup>
import type {
    Product,
    SortOption,
    Facet,
    LandingPage,
    SearchPage,
} from '@apptus/esales-api';
import { watchDebounced } from '@vueuse/core';
import { useGtm } from '@gtm-support/vue-gtm';

const properties = withDefaults(defineProps<{
    as?: string
    campaignOnly?: boolean
    type?: 'category' | 'search'
    query?: string
    origin?: string
    loadAds?: boolean
    clearFilters?: boolean
}>(), {
    as: 'div',
    type: 'category',
    query: undefined,
    origin: undefined,
});

const route = useRoute();
const router = useRouter();
const { getLandingPage, getSearchResults } = useElevate();
const gtm = useGtm();

// NOTE: This component should never be used at multiple places on the same page! It syncs its state with the URL. If we wanted to change things, we should make URL syncing be optional and provide an extra state key prefix as a prop.

// Our product list (provided by `useElevate`'s functions) mostly depends only on the URL of the page (either directly or through our locally-stored refs such as `page`, which is synced both ways with the URL), and also some props passed to the component. This should currently be enough to ensure we never have clashes with the key between different elevate product lists.
const stateBaseKey = `elevate-product-list.${route.fullPath}.${properties.origin}.${properties.query}`;

const stateKeys = {
    products: `${stateBaseKey}.products`,
    isLoading: `${stateBaseKey}.isLoading`,
    alternativeProducts: `${stateBaseKey}.alternativeProducts`,
    facets: `${stateBaseKey}.facets`,
    productCount: `${stateBaseKey}.productCount`,
    sortOptions: `${stateBaseKey}.sortOptions`,
    resetProducts: `${stateBaseKey}.resetProducts`,
};

// These are all set by the `loadProducts` routine. We use `useState` for them so that their values carry over from SSR to hydration so that we don't need to do a new loading of products on the CSR setup.
// NOTE: These are cleared on component unmount. Otherwise nuxt will store the values by the state key indefinitely (not just during hydration), so navigating to a different page and then back to this URL will give you old data. This means the list won't update on client-side navigation, which isn't nice (not a huge problem here, but could be devastating in other kinds of use cases!), and it leaves much more room for state key errors, where if your state key didn't include everything you'll see a different page's products on the list (though that shouldn't be an issue here since our state key is solid, just try to be extra cautious).
const products = useState<Product[]>(stateKeys.products, () => []);
const isLoading = useState<boolean>(stateKeys.isLoading, () => true);
const alternativeProducts = useState<Product[]>(stateKeys.alternativeProducts, () => []);
const facets = useState<Facet[]>(stateKeys.facets, () => []);
const productCount = useState(stateKeys.productCount, () => 0);
const sortOptions = useState<SortOption[]>(stateKeys.sortOptions, () => []);
const resetProducts = useState(stateKeys.resetProducts, () => true);

// This and others are read from the URL on init, they don't need to be stored with `useState` since they'll be correct and match the state values as long as our state key works.
const page = ref(route.query.page ? Number.parseInt(route.query.page?.toString(), 10) : 1);

// If no sorting in URL, default to an empty value which causes Elevate to return results sorted by its configured default sorting method.
const sorting = ref(route.query.sort?.toString() || 'RELEVANCE');

const activeFilters = ref<Record<string, string[]>>(
    Object.fromEntries(
        Object.entries(route.query as Record<string, string>)
            .filter(([key]) => key.startsWith('f-'))
            .map(([key, value]) => [
                key.replace(/^(f-)/, ''),
                value.length > 0 ? value.split('|') : [],
            ]),
    ),
);

const hasNextPage = computed(() => products.value.length < productCount.value);

const activeFiltersWithoutEmptyValues = computed(() => {
    const result: Record<string, string[]> = {};

    for (const [key, value] of Object.entries(activeFilters.value)) {
        if (value !== undefined && value?.length > 0) {
            result[key] = value;
        }
    }

    return result;
});

const activeFiltersCount = computed(() => Object.keys(activeFiltersWithoutEmptyValues.value).length);

// Using the filters value that has empty values filtered out ensures all undefined or empty string filter values will be cleared from the URL query, thus we won't get stuff like `foo=undefined` in the URL.
// eslint-disable-next-line unicorn/no-unreadable-array-destructuring
watchDebounced([activeFiltersWithoutEmptyValues, page, sorting], ([filtersValue, pageValue, sortingValue]) => {
    router.replace({
        path: route.path,
        query: {
            ...Object.fromEntries(
                Object.entries(filtersValue)
                    .filter(([, value]) => value && value.length > 0)
                    .map(([key, value]) => [
                        `f-${key}`,
                        value?.join('|'),
                    ]),
            ),
            page: pageValue > 1 ? pageValue : undefined,
            query: properties.query,
            sort: sortingValue ?? undefined,
        },
    });
}, {
    deep: true,
    debounce: 100,
});

provide('activeFilters', activeFilters);
provide('facets', readonly(facets));
provide('sorting', {
    sorting: readonly(sorting),
    update: (value: string) => {
        sorting.value = value;
    },
});
provide('sortOptions', readonly(sortOptions));

const listingAdsContent = properties.loadAds ? await useAsyncStoryblokStory('global/category-ads') : undefined;
const listingAds = listingAdsContent?.content.ads || [];
const adStep = 12;

function getAdForIndex(index: number) {
    if (!properties.loadAds || index === 0 || index % adStep !== 0) {
        return undefined;
    }

    const adIndex = Math.floor(index / adStep - 1) % listingAds.length;

    return listingAds[adIndex];
}

function calculateSkipAndTakeValues(catchUp: boolean, pageNumber: number) {
    const productsPerBlock = adStep;
    const itemsPerBlock = adStep + 1;
    const itemsPerPage = 24;

    // Calculate the total number of items (products + ads) up to the start of the requested page
    const startOffset = catchUp ? 0 : itemsPerPage * (pageNumber - 1);

    // Calculate the total number of items (products + ads) by the end of the requested page
    const endOffset = itemsPerPage * pageNumber;

    if (!properties.loadAds || listingAds.length === 0) {
        return {
            skip: startOffset,
            take: endOffset - startOffset,
        };
    }

    // Calculate products needed up to the start of the page
    const skip = Math.floor(startOffset / itemsPerBlock)
        * productsPerBlock
        + Math.min(startOffset % itemsPerBlock, productsPerBlock);

    // Calculate products needed by the end of the page
    const productsAtEnd = Math.floor(endOffset / itemsPerBlock)
        * productsPerBlock
        + Math.min(endOffset % itemsPerBlock, productsPerBlock);

    // Calculate the number of products to take for this page
    const take = productsAtEnd - skip;

    return { skip, take };
}

function showCtaForIndex(index: number) {
    if (!properties.loadAds || listingAds.length === 0) {
        return index > 0 && index % 12 === 0;
    }

    // Calculate how many ads are before this product index
    const adsBefore = Math.floor((index - 1) / adStep);

    // Calculate the real index in the combined list of products and ads
    const listIndex = index + adsBefore;

    // Check if the real index is a position where a CTA should be shown
    return listIndex % adStep === 0;
}

async function loadProducts(catchUp = false) {
    isLoading.value = true;

    try {
        let data: LandingPage | SearchPage | null;

        const { skip, take } = calculateSkipAndTakeValues(catchUp, page.value);

        if (properties.type === 'category') {
            const response = await getLandingPage(
                route.path,
                skip,
                take,
                sorting.value,
                activeFiltersWithoutEmptyValues.value,
            );

            data = response.data.value;
        } else {
            const response = await getSearchResults(
                properties.query || '+',
                properties.origin,
                skip,
                take,
                sorting.value,
                activeFiltersWithoutEmptyValues.value,
            );

            data = response.data.value;

            alternativeProducts.value = data?.secondaryList?.productGroups.flatMap((group) => group.products) || [];
        }

        productCount.value = data?.primaryList?.totalHits || 0;
        facets.value = data?.primaryList?.facets || [];
        sortOptions.value = data?.primaryList?.sort.options || [];

        products.value = [
            ...resetProducts.value ? [] : products.value,
            ...data?.primaryList?.productGroups.flatMap((group) => group.products) || [],
        ];

        resetProducts.value = false;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
    }

    isLoading.value = false;
}

watchDebounced(products, (productsValue) => {
    if (productsValue.length === 0) {
        return;
    }

    const gtmItems = productsValue.map((product, index) => {
        const { getStringValues } = useElevateProduct(product);

        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            item_id: getStringValues('sku')[0],
            item_unique_id: getStringValues('id')[0],
            item_name: product.title,
            ...product.brand ? { item_brand: product.brand } : {},
            price: product.sellingPrice.min,
            quantity: 1,
            index,
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    });

    gtm?.trackEvent({
        /* eslint-disable @typescript-eslint/naming-convention */
        event: 'view_item_list',
        _clear: true,
        ecommerce: {
            listed_items: gtmItems,
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    });
}, { debounce: 500 });

// eslint-disable-next-line unicorn/no-unreadable-array-destructuring
watch([activeFilters, sorting, () => properties.query], ([,, newQuery], [,, oldQuery]) => {
    page.value = 1;
    resetProducts.value = true;

    if (newQuery !== oldQuery) {
        activeFilters.value = {};
    }
}, {
    deep: true,
});

watch([page, activeFilters, sorting, () => properties.query], async () => {
    await loadProducts();
}, { deep: true });

function loadMore() {
    page.value += 1;
}

const clearFilter = () => {
    activeFilters.value = {};
};

provide('resetFilters', clearFilter);

// Check if the products state has already been initialized during SSR, if it has we don't want to re-fetch the list on CSR since everything is already there.
if (products.value.length === 0) {
    await loadProducts(true);
}

// See comments earlier in the file
onUnmounted(() => {
    try {
        clearNuxtState(Object.values(stateKeys));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
    }
});
</script>
